var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MenuItem, Link as MuiLink } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PlanType, StatusPermission, lowestPermission } from '../../enum';
import { RumEntityLabel, rumEntityName } from '../../observability/rumEntity';
import { EditScenarioDialog } from '../annual/editDialog';
import { useUserPermissionsAPI } from '../app/selectors';
import { ColumnCustomizer, ColumnFilterHeader, DataGrid, FallbackComponent, MenuButton, useColumnPreferences, useDraggableColumns, useServerTableFilter, } from '../common';
import { useDialog } from '../hooks';
import { DeletePlanDialog } from './deletePlan';
import { DuplicatePlanDialog } from './duplicatePlan';
import { usePlansAPI } from './selectors';
var DialogMode;
(function (DialogMode) {
    DialogMode[DialogMode["Delete"] = 0] = "Delete";
    DialogMode[DialogMode["Duplicate"] = 1] = "Duplicate";
    DialogMode[DialogMode["Edit"] = 2] = "Edit";
})(DialogMode || (DialogMode = {}));
var PLAN_TABLE_KEY = 'myPlansTable';
var PLAN_TABLE_DEFAULT_FIELDS = ['name', 'startDate', 'planType', 'periodLength', 'status', 'lastEdited'];
export var RowMenu = function (_a) {
    var _b, _c;
    var data = _a.data, handleOpenDialog = _a.handleOpenDialog;
    var t = useTranslation('myPlans').t;
    var permission = useUserPermissionsAPI();
    var isCapacityPermission = lowestPermission((_b = permission === null || permission === void 0 ? void 0 : permission.capacityPermission) !== null && _b !== void 0 ? _b : StatusPermission.Edit, (_c = data.effectivePermission) !== null && _c !== void 0 ? _c : StatusPermission.Edit) === StatusPermission.Edit;
    var isLocked = !!data.isLocked;
    return (_jsxs(MenuButton, __assign({ disabled: !data.status && !isCapacityPermission }, { children: [_jsx(MenuItem, __assign({ component: Link, to: "/report/".concat(data.basePlanId), disabled: !data.status }, { children: t('viewReport') })), _jsx(MenuItem, __assign({ disabled: !isCapacityPermission || isLocked, onClick: handleOpenDialog.bind(null, DialogMode.Duplicate, data) }, { children: t('duplicate') })), _jsx(MenuItem, __assign({ disabled: !isCapacityPermission || isLocked, onClick: handleOpenDialog.bind(null, DialogMode.Delete, data) }, { children: t('delete') }))] })));
};
var useColumns = function (_a) {
    var handleOpenDialog = _a.handleOpenDialog, order = _a.order, hidden = _a.hidden, onUpdate = _a.onUpdate, onReset = _a.onReset, getFilterState = _a.getFilterState, getAllValues = _a.getAllValues, applyFilter = _a.applyFilter;
    var t = useTranslation(['myPlans', 'translation']).t;
    var navigate = useNavigate();
    var permission = useUserPermissionsAPI();
    var makeDraggableHeader = useDraggableColumns(order, hidden, onUpdate).makeDraggableHeader;
    var renderColumnHeader = useCallback(function (field, label) {
        var filterHeader = (_jsx(ColumnFilterHeader, { label: label, filterState: getFilterState(field), allValues: getAllValues(field), onApply: function (update) { return applyFilter(field, update); } }));
        return field === 'name' ? filterHeader : makeDraggableHeader(field, filterHeader);
    }, [applyFilter, getAllValues, getFilterState, makeDraggableHeader]);
    var allDataColumns = useMemo(function () { return [
        {
            field: 'name',
            headerName: t('nameOfPlan'),
            flex: 5,
            sortable: false,
            renderHeader: function () { return renderColumnHeader('name', t('nameOfPlan')); },
            renderCell: function (params) {
                var _a, _b;
                return lowestPermission((_a = permission === null || permission === void 0 ? void 0 : permission.capacityPermission) !== null && _a !== void 0 ? _a : StatusPermission.Edit, (_b = params.row.effectivePermission) !== null && _b !== void 0 ? _b : StatusPermission.Edit) === StatusPermission.Edit ? (_jsx(MuiLink, __assign({ component: "button", onClick: params.row.hasMultiskill
                        ? handleOpenDialog.bind(null, DialogMode.Edit, params.row)
                        : function () { return navigate("/wizard/".concat(params.row.basePlanId)); }, title: params.value, "data-dd-action-name": "Open plan: ".concat(rumEntityName('plan', params.row.id)) }, { children: _jsx(RumEntityLabel, __assign({ kind: "plan", id: params.row.id, hasActionName: false }, { children: params.value })) }))) : (_jsx(RumEntityLabel, __assign({ kind: "plan", id: params.row.id }, { children: params.value })));
            },
        },
        {
            field: 'startDate',
            headerName: t('planStartDate'),
            flex: 2,
            sortable: false,
            renderHeader: function () { return renderColumnHeader('startDate', t('planStartDate')); },
            valueFormatter: function (params) { return moment.utc(params.value).format(t('dateFormat.short', { ns: 'translation' })); },
        },
        {
            field: 'planType',
            headerName: t('planType'),
            flex: 2,
            sortable: false,
            renderHeader: function () { return renderColumnHeader('planType', t('planType')); },
            valueGetter: function (params) { return params.value; },
            valueFormatter: function (params) {
                return params.value === PlanType.MonthlyCustom
                    ? t(PlanType.Monthly, { ns: 'translation' })
                    : t(params.value, { defaultValue: params.value, ns: 'translation' });
            },
        },
        {
            field: 'periodLength',
            headerName: t('length'),
            flex: 1.5,
            sortable: false,
            renderHeader: function () { return renderColumnHeader('periodLength', t('length')); },
            valueGetter: function (params) { return params.row; },
            valueFormatter: function (params) {
                return t("".concat(params.value.periodLength === 1 ? 'singular' : 'plural', "Length").concat(params.value.planType), {
                    length: params.value.periodLength,
                });
            },
        },
        {
            field: 'status',
            headerName: t('status'),
            flex: 1.5,
            sortable: false,
            // Status is display-only (expensive to compute at scale): no sort/filter, plain draggable header.
            renderHeader: function () { return renderColumnHeader('status', t('status')); },
            valueGetter: function (_a) {
                var value = _a.value;
                return t('status', { context: value.toString() });
            },
        },
        {
            field: 'lastEdited',
            headerName: t('lastEdited'),
            flex: 2,
            type: 'dateTime',
            sortable: false,
            renderHeader: function () { return renderColumnHeader('lastEdited', t('lastEdited')); },
            valueFormatter: function (params) { return moment(params.value).format(t('dateFormat.short', { ns: 'translation' })); },
        },
    ]; }, [handleOpenDialog, navigate, permission === null || permission === void 0 ? void 0 : permission.capacityPermission, renderColumnHeader, t]);
    var customizerColumnDefs = useMemo(function () { return allDataColumns.map(function (col) { return ({ field: col.field, headerName: col.headerName }); }); }, [allDataColumns]);
    return useMemo(function () {
        var orderedVisible = order
            .filter(function (field) { return !hidden.includes(field); })
            .map(function (field) { return allDataColumns.find(function (col) { return col.field === field; }); })
            .filter(Boolean);
        return __spreadArray([
            {
                field: 'id',
                headerName: '',
                flex: 0.5,
                sortable: false,
                headerAlign: 'center',
                renderHeader: function () { return (_jsx(ColumnCustomizer, { columns: customizerColumnDefs, lockedField: "name", order: order, hidden: hidden, onUpdate: onUpdate, onReset: onReset, customizeLabel: t('customizeColumns.tooltip'), resetLabel: t('customizeColumns.reset') })); },
                renderCell: function (params) { return _jsx(RowMenu, { data: params.row, handleOpenDialog: handleOpenDialog }); },
            }
        ], orderedVisible, true);
    }, [allDataColumns, customizerColumnDefs, handleOpenDialog, hidden, onReset, onUpdate, order, t]);
};
export var PlanTable = function (_a) {
    var _b, _c, _d, _e, _f;
    var _g = _a.searchQuery, searchQuery = _g === void 0 ? '' : _g, onClearSearch = _a.onClearSearch;
    var t = useTranslation(['myPlans', 'translation']).t;
    var _h = useDialog(), dialogMode = _h.dialogMode, selectedPlan = _h.data, handleOpenDialog = _h.handleOpenDialog, handleCloseDialog = _h.handleCloseDialog;
    var _j = usePlansAPI(), plansData = _j[0], fetchPlans = _j[1], clearPlansData = _j[2];
    var permission = useUserPermissionsAPI();
    var _k = useColumnPreferences(PLAN_TABLE_KEY, PLAN_TABLE_DEFAULT_FIELDS), prefs = _k.prefs, update = _k.update, reset = _k.reset;
    // Server-side filter/sort config. `status` is intentionally omitted (display-only).
    var filterColumns = useMemo(function () { return [
        { field: 'name', filter: 'text', sortable: true },
        { field: 'startDate', filter: 'date', sortable: true },
        {
            field: 'planType',
            filter: 'enum',
            sortable: true,
            getLabel: function (value) {
                return value === PlanType.MonthlyCustom
                    ? t(PlanType.Monthly, { ns: 'translation' })
                    : t(value, { ns: 'translation' });
            },
        },
        { field: 'periodLength', sortable: true },
        { field: 'lastEdited', filter: 'date', sortable: true },
    ]; }, [t]);
    var _l = useServerTableFilter({ columns: filterColumns, facets: (_b = plansData.data) === null || _b === void 0 ? void 0 : _b.facets, search: searchQuery }), queryParams = _l.queryParams, sortModel = _l.sortModel, paginationModel = _l.paginationModel, onPaginationModelChange = _l.onPaginationModelChange, getFilterState = _l.getFilterState, getAllValues = _l.getAllValues, applyFilter = _l.applyFilter, resetFilters = _l.resetFilters;
    useEffect(function () {
        fetchPlans(undefined, queryParams);
    }, [fetchPlans, queryParams]);
    useEffect(function () { return clearPlansData; }, [clearPlansData]);
    var resetTableState = useCallback(function () {
        resetFilters();
        onClearSearch === null || onClearSearch === void 0 ? void 0 : onClearSearch();
    }, [resetFilters, onClearSearch]);
    var columns = useColumns({
        handleOpenDialog: handleOpenDialog,
        order: prefs.order,
        hidden: prefs.hidden,
        onUpdate: update,
        onReset: reset,
        getFilterState: getFilterState,
        getAllValues: getAllValues,
        applyFilter: applyFilter,
    });
    return (_jsxs(FallbackComponent, { children: [_jsx(DataGrid, { columns: columns, rows: plansData.requesting ? [] : (_d = (_c = plansData.data) === null || _c === void 0 ? void 0 : _c.items) !== null && _d !== void 0 ? _d : [], rowCount: (_f = (_e = plansData.data) === null || _e === void 0 ? void 0 : _e.total) !== null && _f !== void 0 ? _f : 0, paginationMode: "server", sortingMode: "server", sortModel: sortModel, paginationModel: paginationModel, onPaginationModelChange: onPaginationModelChange, autoHeight: true, localeText: {
                    columnHeaderSortIconLabel: t('table.sortIconLabel', { defaultValue: 'Sort' }),
                }, loading: plansData.requesting !== false, getRowClassName: function (_a) {
                    var _b, _c;
                    var row = _a.row;
                    return lowestPermission((_b = permission === null || permission === void 0 ? void 0 : permission.capacityPermission) !== null && _b !== void 0 ? _b : StatusPermission.Edit, (_c = row.effectivePermission) !== null && _c !== void 0 ? _c : StatusPermission.Edit) !== StatusPermission.Edit && !row.status
                        ? 'disabled'
                        : '';
                } }), _jsx(DeletePlanDialog, { open: dialogMode === DialogMode.Delete, plan: selectedPlan, onClose: handleCloseDialog, resetTableState: resetTableState }), _jsx(DuplicatePlanDialog, { open: dialogMode === DialogMode.Duplicate, plan: selectedPlan, onClose: handleCloseDialog, resetTableState: resetTableState }), _jsx(EditScenarioDialog, { scenario: selectedPlan, open: dialogMode === DialogMode.Edit, to: "/wizard/".concat(selectedPlan === null || selectedPlan === void 0 ? void 0 : selectedPlan.basePlanId), onClose: handleCloseDialog })] }));
};
